import React, {KeyboardEvent, ReactElement} from "react";
import {debounce} from "throttle-debounce";

import {css, generateKeyId} from "/src/utils/utils";
import translate from "/src/utils/translate";
import {visibleForLocale} from "/src/utils/utils";
import KEY from "/src/constants/keyboard-keydown-key";
import {LOCALE} from "/src/config";

import LinkTag from "../../buttons/link-tag/LinkTag";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import Icon from "../../icon/Icon";
import SearchDrawer from "../../search/drawer/SearchDrawer";
import NavigationItem from "../navigation-item/NavigationItem";
import MobileNavigationItems from "../mobile-navigation-items/MobileNavigationItems";

import {LayoutContext} from "../../layouts/layout/Layout";

import FlexportLogo from "/src/svg/flexport-light.svg";
import FlexportCNLogo from "/src/svg/flexport-cn-light.svg";
import SearchIcon from "/src/svg/search.svg";

import * as styles from "./navigation-items.module.scss";

type NavNodes =
  Queries.ContentfulNavComponentQueryQuery["allContentfulSuperMainNavBars"]["nodes"][0];

interface NavigationItemsProps {
  navTabs: NavNodes["navTabs"];
  primaryCTA?: CTA;
  secondaryCTA?: CTA;
  locale?: string;
}

export const NAV_ITEMS_ELEM_ID = "flexport-nav-items";
export const SEARCH_DRAWER_ID = "flexport-main-nav-search-panel";

const NavigationItems = (props: NavigationItemsProps): ReactElement => {
  const {navTabs, primaryCTA, secondaryCTA, locale = LOCALE} = props;

  const searchTriggerRef = React.useRef<null | HTMLButtonElement>(null);
  const [searchVisible, setSearchVisible] = React.useState<boolean>(false);
  const {hideNavSearch, hideRightNav} = React.useContext(LayoutContext);
  const [focusNavIndex, setFocusNavIndex] = React.useState<number>(-1);
  const navItemsRef = React.useRef<null | HTMLUListElement>(null);
  const currentFocusedMenuItem = React.useRef<HTMLElement | null>(null);
  const mounted = React.useRef<boolean>(false);
  const menuItems = React.useRef<Array<HTMLElement | null> | null>(null);

  const setTabIndex = (elem: HTMLElement | null, tabIndex: number) => {
    if (elem !== null) {
      // eslint-disable-next-lint no-param-reassign
      elem.tabIndex = tabIndex;
    }
  };

  const debouncedFocusNavItemByIndex = debounce(
    100,
    (idx: number, isKeyboardEvent: boolean = false) => {
      if (!menuItems.current) {
        return;
      }

      // focus to last nav
      const focusMenuItem = menuItems.current[idx];

      // remove current tabindex
      if (currentFocusedMenuItem.current) {
        currentFocusedMenuItem.current.blur();
        setTabIndex(currentFocusedMenuItem.current, -1);
      }

      if (focusMenuItem) {
        focusMenuItem.tabIndex = 0;

        if (isKeyboardEvent) {
          // we only want to focus on keyboard event
          // this will show focus-visible style
          focusMenuItem.focus();
        }

        setFocusNavIndex(idx);
        // prevFocusedNavItem.current = idx;
        currentFocusedMenuItem.current = focusMenuItem;
      }
    },
    {atBegin: false}
  );

  const debouncedResetFocusState = debounce(
    100,
    () => {
      // set back item 1 tab index
      if (!menuItems.current) {
        return;
      }

      const focusMenuItem = menuItems.current[0];

      // remove current tabindex
      if (currentFocusedMenuItem.current) {
        currentFocusedMenuItem.current.blur();
        setTabIndex(currentFocusedMenuItem.current, -1);
      }

      if (focusMenuItem) {
        focusMenuItem.tabIndex = 0;
        setFocusNavIndex(-1);
        currentFocusedMenuItem.current = focusMenuItem;
      }
    },
    {atBegin: false}
  );

  const handleOnKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    if (!menuItems.current) {
      return;
    }

    const lastMenuIdx = menuItems.current.length - 1;
    let preventDefault = true;

    switch (e.key) {
      case KEY.LEFT:
        {
          // focus to previous nav item
          const focusIndex =
            focusNavIndex <= 0 ? lastMenuIdx : focusNavIndex - 1;

          debouncedFocusNavItemByIndex(focusIndex, true);
        }
        break;

      case KEY.RIGHT:
        {
          const focusIndex =
            focusNavIndex === lastMenuIdx ? 0 : focusNavIndex + 1;

          debouncedFocusNavItemByIndex(focusIndex, true);
        }
        break;

      case KEY.PAGEUP:
      case KEY.HOME:
        // focus to first nav item
        debouncedFocusNavItemByIndex(0, true);
        break;

      case KEY.END:
      case KEY.PAGEDOWN:
        // focus to last nav item
        debouncedFocusNavItemByIndex(lastMenuIdx, true);
        break;

      case KEY.ESC:
        // focus to previous nav item
        if (currentFocusedMenuItem.current) {
          currentFocusedMenuItem.current.focus();
          setTabIndex(currentFocusedMenuItem.current, 0);
        }
        break;

      // tab should close dropdown menu and move to next item outside of nav
      case KEY.TAB:
        debouncedResetFocusState();
        preventDefault = false;
        break;

      case KEY.ENTER: {
        const $target: HTMLElement = e.target as HTMLElement;
        // if element is anchor tag, set prevent default as false
        if ($target && $target.tagName === "A") {
          preventDefault = false;
        }
        break;
      }

      default:
        break;
    }

    if (preventDefault) {
      // we don't want event to bubble up/down
      e.preventDefault();
    }
  };

  const handleOnFocus = () => {
    // on menu bar focus, if nav index is -1
    // let's focus from first item
    if (focusNavIndex === -1) {
      debouncedFocusNavItemByIndex(0, true);
    }
  };

  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    }

    if (navItemsRef.current) {
      // we can't query by class name, as when building, it will gets compiled to random classname
      // this is a long winded way to grab the main nav items element
      const navList = navItemsRef.current.querySelectorAll(
        "li[data-role='menuitem']"
      );

      const items: Array<HTMLElement | null> = [];

      Array.from(navList).forEach(item => {
        items.push(item.querySelector("[role='menuitem']:first-child"));
      });
      menuItems.current = items;
    }
  });

  return (
    <div className={styles.navigationItems}>
      <ModuleBody classes={styles.navigationItems__body}>
        <LinkTag
          ariaLabel="Flexport"
          href="/"
          classes={styles.navigationItems__logoLink}
        >
          <Icon
            svg={locale === "zh-CN" ? FlexportCNLogo : FlexportLogo}
            classes={styles.navigationItems__logo}
          />
        </LinkTag>

        {navTabs && (
          <nav className={styles.navigationItems__nav}>
            <ul
              id={NAV_ITEMS_ELEM_ID}
              ref={navItemsRef}
              onFocus={handleOnFocus}
              onKeyDown={handleOnKeyDown}
              role="menubar"
              tabIndex={0}
              className={styles.navigationItems__nav__list}
            >
              {navTabs.map((tab, i) => {
                const visibleIn: Array<string> =
                  tab && tab.visibleIn ? (tab.visibleIn as Array<string>) : [];

                if (!visibleForLocale(visibleIn, locale)) {
                  return null;
                }

                const elemKey = generateKeyId(
                  `nav-items-${tab?.tabTitle!}-${i}`
                );

                return (
                  <li
                    key={elemKey}
                    role="none"
                    data-role="menuitem"
                    className={css(styles.navigationItems__nav__item)}
                  >
                    <NavigationItem
                      onMouseLeave={() => debouncedResetFocusState()}
                      onMouseEnter={() => debouncedFocusNavItemByIndex(i)}
                      navTab={tab}
                    />
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
        <div className={css(styles.navigationItems__right)}>
          {!hideNavSearch && (
            <button
              ref={searchTriggerRef}
              type="button"
              aria-label={translate(
                searchVisible ? "Close search panel" : "Show search panel"
              )}
              aria-expanded={!searchVisible}
              aria-controls={SEARCH_DRAWER_ID}
              className={css(styles.navigationItems__right__searchBtn)}
              onClick={() => setSearchVisible(!searchVisible)}
            >
              <Icon
                svg={SearchIcon}
                classes={styles.navigationItems__right__searchIcon}
              />
            </button>
          )}
          {secondaryCTA && !hideRightNav && (
            <CTAButton
              kind="tertiary"
              cta={secondaryCTA}
              darkMode
              small
              classes={styles.navigationItems__right__secondaryCTA}
            />
          )}
          {primaryCTA && !hideRightNav && (
            <CTAButton
              kind="primary"
              darkMode
              cta={primaryCTA}
              small
              classes={styles.navigationItems__right__CTA}
            />
          )}
        </div>
      </ModuleBody>

      <MobileNavigationItems
        navTabs={navTabs}
        primaryCTA={hideRightNav ? undefined : primaryCTA}
        onSearch={() => {
          setSearchVisible(!searchVisible);
        }}
        searchVisible={searchVisible}
      />

      {/* load search drawer */}
      {!hideNavSearch && (
        <SearchDrawer
          onClose={() => {
            setSearchVisible(false);

            // wait for next paint then try to focus on button
            requestAnimationFrame(() => {
              if (searchTriggerRef.current) {
                searchTriggerRef.current.focus();
              }
            });
          }}
          open={searchVisible}
          id={SEARCH_DRAWER_ID}
        />
      )}
    </div>
  );
};

export default NavigationItems;
